"use client";

/**
 * THE DENSITY AXIS — how tight the chrome sits, independent of face and hue.
 *
 * `compact` is the default (the Linear register: 6px radius, 32px controls,
 * 1px focus ring) and lives in `:root`; `comfortable` is the one
 * `[data-density]` block in globals.css. Same construction as `hue.ts`:
 * boot script before paint, `useSyncExternalStore` mirror, one storage
 * key, attribute removed for the default. Primitives never hard-code a
 * control height — they read `--control-h`, `--radius`, `--ring-w`.
 */

import { useSyncExternalStore } from "react";

export const DENSITIES = ["compact", "comfortable"] as const;
export type Density = (typeof DENSITIES)[number];
export const DEFAULT_DENSITY: Density = "compact";
export const DENSITY_STORAGE_KEY = "hh.density";

const LABELS: Record<Density, string> = {
  compact: "Compact",
  comfortable: "Comfortable",
};

export function labelForDensity(d: Density): string {
  return LABELS[d];
}

export function isKnownDensity(value: string): value is Density {
  return (DENSITIES as readonly string[]).includes(value);
}

export const DENSITY_BOOT_SCRIPT = `(function(){try{var v=localStorage.getItem(${JSON.stringify(
  DENSITY_STORAGE_KEY,
)});var ok=${JSON.stringify([...DENSITIES])};var d=document.documentElement;if(v&&ok.indexOf(v)>-1&&v!==${JSON.stringify(
  DEFAULT_DENSITY,
)}){d.setAttribute("data-density",v)}else{d.removeAttribute("data-density")}}catch(e){}})()`;

const listeners = new Set<() => void>();

function readDensity(): Density {
  if (typeof window === "undefined") return DEFAULT_DENSITY;
  try {
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored && isKnownDensity(stored) ? stored : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

function applyDensity(d: Density): void {
  const root = document.documentElement;
  if (d === DEFAULT_DENSITY) root.removeAttribute("data-density");
  else root.setAttribute("data-density", d);
}

export function setDensity(d: Density): void {
  applyDensity(d);
  try {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, d);
  } catch {
    // storage refused — the attribute still applied for this session.
  }
  for (const notify of listeners) notify();
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  const onStorage = (e: StorageEvent) => {
    if (e.key === DENSITY_STORAGE_KEY) {
      applyDensity(readDensity());
      notify();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(notify);
    window.removeEventListener("storage", onStorage);
  };
}

export function useDensity(): { density: Density; setDensity: (d: Density) => void } {
  const density = useSyncExternalStore(subscribe, readDensity, () => DEFAULT_DENSITY);
  return { density, setDensity };
}
